import { Injectable } from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import { Technician } from '../model/technician';
import { FindHttpError } from '../error/find-http-error';
import { Empty } from '../error/empty';
import { DeleteHttpError } from '../error/delete-http-error';
import { HttpError } from '../error/http-error';
import { SaveHttpError } from '../error/save-http-error';
import { UpdateHttpError } from '../error/update-http-error';
import { HttpService } from './http.service';
import { NgRedux } from '@angular-redux/store';
import { ArgosState } from '../../core/argos-state';
import { PageInfo } from '../model/page-info';

@Injectable()
export class TechnicianService {
  private static TECHNICIAN_END_POINT = '/technician';

  private SAVE = `${TechnicianService.TECHNICIAN_END_POINT}/save`;
  private UPDATE = `${TechnicianService.TECHNICIAN_END_POINT}/update`;
  private DISABLE = `${TechnicianService.TECHNICIAN_END_POINT}/disable/`;
  private FIND_BY_FILTER = `${TechnicianService.TECHNICIAN_END_POINT}/findByFilter/`;
  private FIND_BY_ID = `${TechnicianService.TECHNICIAN_END_POINT}/findById/`;
  private FIND_FREE_TECHNICIANS = `${TechnicianService.TECHNICIAN_END_POINT}/freeTechnicians`;
  private FIND_PAGED_TECHNICIANS = `${TechnicianService.TECHNICIAN_END_POINT}/findList/`;
  private BY_PROVIDED_SERVICES_AND_AE_ID = `${TechnicianService.TECHNICIAN_END_POINT}/findByProvidedServicesAndAutoEletricId`;
  private FIND_PAGE_INFO = `${TechnicianService.TECHNICIAN_END_POINT}/findPageInfo/`;

  save$: Observable<Technician>;
  update$: Observable<Technician>;
  find$: Observable<Technician | Array<Technician>>;
  delete$: Observable<Empty>;
  pageInfo$: Observable<PageInfo>;
  error$: Observable<HttpError>;

  private saveSubject: Subject<Technician>;
  private updateSubject: Subject<Technician>;
  private findSubject: Subject<Technician | Array<Technician>>;
  private deleteSubject: Subject<Empty>;
  private pageInfoSubject: Subject<PageInfo>;
  private errorSubject: Subject<HttpError>;

  constructor(private _httpService: HttpService,
              private _ngRedux: NgRedux<ArgosState>) {
    this.saveSubject = new Subject();
    this.save$ = this.saveSubject.asObservable();
    this.updateSubject = new Subject();
    this.update$ = this.updateSubject.asObservable();
    this.findSubject = new Subject();
    this.find$ = this.findSubject.asObservable();
    this.deleteSubject = new Subject();
    this.delete$ = this.deleteSubject.asObservable();
    this.pageInfoSubject = new Subject();
    this.pageInfo$ = this.pageInfoSubject.asObservable();
    this.errorSubject = new Subject();
    this.error$ = this.errorSubject.asObservable();
  }

  save(technician: Technician) {
    if (!technician.vehicle.plate && !technician.vehicle.model && !technician.vehicle.year && !technician.vehicle.color) {
      technician.vehicle = null;
    }
    this._httpService.post(this.SAVE, technician, Technician, SaveHttpError)
      .subscribe(
        tech => this.saveSubject.next(tech),
        error => this.errorSubject.next(error)
      );
  }

  update(technician: Technician) {
    if (!technician.vehicle.plate && !technician.vehicle.model && !technician.vehicle.year && !technician.vehicle.color) {
      technician.vehicle = null;
    }
    this._httpService.put(this.UPDATE, technician, Technician, UpdateHttpError)
      .subscribe(
        tech => this.updateSubject.next(tech),
        error => this.errorSubject.next(error)
      );
  }

  findById(id: number) {
    this._httpService.get(`${this.FIND_BY_ID}${id}`, Technician, FindHttpError)
      .subscribe(
        technician => this.findSubject.next(technician),
        error => this.errorSubject.next(error)
      );
  }

  findByProvidedServicesAndAutoEletricId(providedServices, autoEletricId) {
    return this._httpService
      .post(`${this.BY_PROVIDED_SERVICES_AND_AE_ID}/${autoEletricId}?&pageNumber=${0}&pageSize=${15}`,
            providedServices, Array, FindHttpError);
  }

  findTechnicians(pageNumber: number, pageSize: number) {
    this._ngRedux.select('technicianFilter')
      .subscribe(filter => {
        this.findByFilter(filter, pageNumber, pageSize);
        this.findPageInfo(filter, pageSize);
      });
  }

  findFreeTechnicians() {
    this._httpService.get(this.FIND_FREE_TECHNICIANS, Array, FindHttpError)
      .subscribe(
        res => this.findSubject.next(res),
        error => this.errorSubject.next(error)
      );
  }

  findByPage(pageNumber, pageSize) {
    this._httpService.get(`${this.FIND_PAGED_TECHNICIANS}${pageNumber}/${pageSize}`, Array, FindHttpError)
      .subscribe(
        technicians => this.findSubject.next(technicians),
        error => this.errorSubject.next(error)
      );
  }

  disable(id: number) {
    this._httpService.delete(`${this.DISABLE}${id}`, DeleteHttpError)
      .subscribe(
        (res) => this.deleteSubject.next(id),
        (error: HttpError) => this.errorSubject.next(error)
      );
  }

  private findPageInfo(filter, pageSize) {
    this._httpService.post(`${this.FIND_PAGE_INFO}?pageSize=${pageSize}`, filter, PageInfo, FindHttpError)
      .subscribe(
        pageInfo => this.pageInfoSubject.next(pageInfo),
        error => this.errorSubject.next(error)
      );
  }

  private findByFilter(filter, pageNumber, pageSize) {
    this._httpService.post(`${this.FIND_BY_FILTER}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
                            filter, Array, FindHttpError)
      .subscribe(
        technicians => this.findSubject.next(technicians),
        error => this.errorSubject.next(error)
      );
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/production-argos-front/argos/src/app/shared/service/technician.service.ts